import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useEnqueueStacks } from '@avangard/utils/hooks';
import { Loader } from '@avangard/ui/core';
import { breakpoints } from '@avangard/ui/utils';

import { ExtendedFormik } from '@modules/layout/organisms';
import { StudentOnlineApplicationStep } from '@modules/types/graphql';
import { useStudentOnlineApplication } from '@modules/students/hooks';
import { UpdateStudentOnlineApplicationMutation } from '@modules/students/graphql';
import { UpdateStudentOnlineApplicationRequest } from '@modules/students/requests';
import { StudentParentEntity } from '@modules/students/entities';
import { StudentOnlineApplicationHeader } from './student-online-application-header';
import {
    StudentOnlineApplicationSpecialsStep,
    StudentOnlineApplicationIntroductionStep,
    StudentOnlineApplicationMedicalStep,
    StudentOnlineApplicationSummaryStep,
    StudentOnlineApplicationDocumentsStep,
} from './student-online-application-steps';
import { StudentOnlineApplicationResult } from './student-online-application-result';
import { StudentOnlineApplicationFooter } from './student-online-application-footer';

import type { UpdateStudentOnlineApplicationFormValues } from '@modules/students/requests';
import type {
    UpdateStudentOnlineApplicationMutationType,
    UpdateStudentOnlineApplicationMutationVariables,
} from '@modules/types/graphql';

const StudentOnlineApplication = () => {
    const { t } = useTranslation(['common']);

    const { enqueueError } = useEnqueueStacks();

    const { studentOnlineApplication, loading: studentOnlineApplicationLoading } =
        useStudentOnlineApplication({
            pollInterval: 10_000,
        });

    const [updateStudentOnlineApplication] = useMutation<
        UpdateStudentOnlineApplicationMutationType,
        UpdateStudentOnlineApplicationMutationVariables
    >(UpdateStudentOnlineApplicationMutation);

    const initialValues = React.useMemo(() => {
        const hasParents = studentOnlineApplication.student.parents.length > 0;

        return {
            originalStudentOnlineApplication: studentOnlineApplication,
            temporaryStep: studentOnlineApplication.step,

            isAdult: studentOnlineApplication.ageLimits.isAdult,
            isFutureAdult: studentOnlineApplication.ageLimits.isFutureAdult,

            currentStep: studentOnlineApplication.step,
            currentStatus: studentOnlineApplication.status,

            introduction: {
                passed: studentOnlineApplication.isPassedIntroduction(),
            },

            summary: {
                responsible: {
                    fullName: studentOnlineApplication.responsible.fullName,
                    birthdate: studentOnlineApplication.responsible.birthdate,
                    passportSerial: studentOnlineApplication.responsible.passportSerial,
                    passportNumber: studentOnlineApplication.responsible.passportNumber,
                    passportIssued: studentOnlineApplication.responsible.passportIssued,
                    passportAddress: studentOnlineApplication.responsible.passportAddress,
                    address: studentOnlineApplication.responsible.address,
                    phone: studentOnlineApplication.responsible.phone,
                    email: studentOnlineApplication.responsible.email,
                    isResponsibleChecked: studentOnlineApplication.responsible.issetAnyField(),
                },

                main: {
                    fullName: studentOnlineApplication.student.fullName,
                    fullNameGenitive: studentOnlineApplication.student.fullNameGenitive,
                    birthdate: studentOnlineApplication.student.birthdate,
                    passportAddress: studentOnlineApplication.student.passportAddress,
                    address: studentOnlineApplication.student.address,
                    passportSerial: studentOnlineApplication.student.passportSerial,
                    passportNumber: studentOnlineApplication.student.passportNumber,
                    passportIssued: studentOnlineApplication.student.passportIssued,
                    schoolName:
                        studentOnlineApplication.student.school.fullName ||
                        studentOnlineApplication.student.school.shortName,

                    studyYear: studentOnlineApplication.nearestLap.getYear(),
                    phone: studentOnlineApplication.student.phone,
                    email: studentOnlineApplication.student.email,
                    vk: studentOnlineApplication.student.socials.vk,
                    telegram: studentOnlineApplication.student.socials.telegram,
                },

                criticalContacts: hasParents
                    ? studentOnlineApplication.student.parents
                    : [new StudentParentEntity({})],

                recreation: {
                    selfLeft: studentOnlineApplication.recreation.selfLeft,
                    count: studentOnlineApplication.recreation.count,
                    fromAge: studentOnlineApplication.recreation.fromAge,
                    assesment: studentOnlineApplication.recreation.assesment,
                    mood: studentOnlineApplication.recreation.mood,
                },
            },

            medical: {
                diets: studentOnlineApplication.student.medical.diets,
                proneToColdBool: studentOnlineApplication.student.medical.proneToColdBool,
                proneToCold: studentOnlineApplication.student.medical.proneToCold,
                fractures: studentOnlineApplication.student.medical.fractures,
                headInjuries: studentOnlineApplication.student.medical.headInjuries,
                allergicDiseases: studentOnlineApplication.student.medical.allergicDiseases,
                allergyMedications: studentOnlineApplication.student.medical.allergyMedications,
                chronicDiseases: studentOnlineApplication.student.medical.chronicDiseases,
                medications: studentOnlineApplication.student.medical.medications,
                heatTransfer: studentOnlineApplication.student.medical.heatTransfer,
                insectBites: studentOnlineApplication.student.medical.insectBites,
                healthSpecials: studentOnlineApplication.student.medical.healthSpecials,
                fatigueBool: studentOnlineApplication.student.medical.fatigueBool,
                fatigue: studentOnlineApplication.student.medical.fatigue,
                motionSicknessBool: studentOnlineApplication.student.medical.motionSicknessBool,
                motionSickness: studentOnlineApplication.student.medical.motionSickness,
                wearingGlassesBool: studentOnlineApplication.student.medical.wearingGlassesBool,
                wearingGlasses: studentOnlineApplication.student.medical.wearingGlasses,
                fearDarkBool: studentOnlineApplication.student.medical.fearDarkBool,
                fearDark: studentOnlineApplication.student.medical.fearDark,
                otherSpecials: studentOnlineApplication.student.medical.otherSpecials,
            },
            individual: {
                wantToServe: studentOnlineApplication.student.individual.wantToServe,
                sports: studentOnlineApplication.student.individual.sports,
                pleasureActivities: studentOnlineApplication.student.individual.pleasureActivities,
                specialSkills: studentOnlineApplication.student.individual.specialSkills,
                unPleasureActivities:
                    studentOnlineApplication.student.individual.unPleasureActivities,
                stressfulSituations:
                    studentOnlineApplication.student.individual.stressfulSituations,
                attitudeThings: studentOnlineApplication.student.individual.attitudeThings,
                selfCare: studentOnlineApplication.student.individual.selfCare,
                needToControlled: studentOnlineApplication.student.individual.needToControlled,
                needToControlledOther:
                    studentOnlineApplication.student.individual.needToControlledOther,
                preferCommunicate: studentOnlineApplication.student.individual.preferCommunicate,
                communationDifficulties:
                    studentOnlineApplication.student.individual.communationDifficulties,
                characterTraits: studentOnlineApplication.student.individual.characterTraits,
                badHabits: studentOnlineApplication.student.individual.badHabits,
            },
        };
    }, [studentOnlineApplication]);

    const handleSubmit = async (values: UpdateStudentOnlineApplicationFormValues) => {
        try {
            const item = new UpdateStudentOnlineApplicationRequest(values);

            await updateStudentOnlineApplication({
                variables: { item },
            });
        } catch {
            enqueueError('При сохранении анкеты произошла ошибка');
        }
    };

    const renderSteps = (currentStep: StudentOnlineApplicationStep) => {
        switch (currentStep) {
            case StudentOnlineApplicationStep.introduction:
                return <StudentOnlineApplicationIntroductionStep />;

            case StudentOnlineApplicationStep.summary:
                return <StudentOnlineApplicationSummaryStep />;

            case StudentOnlineApplicationStep.medical:
                return <StudentOnlineApplicationMedicalStep />;

            case StudentOnlineApplicationStep.specials:
                return <StudentOnlineApplicationSpecialsStep />;

            case StudentOnlineApplicationStep.documents:
                return <StudentOnlineApplicationDocumentsStep />;

            default:
                return null;
        }
    };

    if (studentOnlineApplicationLoading) {
        return (
            <Root>
                <StyledLoader />
            </Root>
        );
    }

    return (
        <Root>
            <ExtendedFormik<UpdateStudentOnlineApplicationFormValues>
                enableReinitialize
                validateOnBlur={false}
                validateOnChange={false}
                initialValues={initialValues}
                validationSchema={() => {
                    const requiredText = t('errors:validations.required') ?? '';
                    const requiredOption = 'Не выбран вариант ответа';

                    const isMinor = studentOnlineApplication.isMinor();

                    const fieldsSummary = {
                        responsible: yup.object(),

                        main: yup.object({
                            passportAddress: yup.string().required(requiredText),
                            address: yup.string().required(requiredText),
                            passportSerial: yup.string().required(requiredText),
                            passportNumber: yup.string().required(requiredText),
                            passportIssued: yup.string().required(requiredText),
                            phone: yup.string().required(requiredText),
                            vk: yup.string(),
                            telegram: yup.string(),
                        }),

                        criticalContacts: yup.array().of(
                            yup.object({
                                name: yup.string().required(requiredText),
                                phone: yup.string().required(requiredText),
                            }),
                        ),

                        recreation: yup.object({
                            selfLeft: yup.string().required(requiredText),
                            count: yup.string().when('selfLeft', {
                                is: '1',
                                then: yup.string().required(requiredText),
                            }),

                            fromAge: yup.string().when('selfLeft', {
                                is: '1',
                                then: yup.string().required(requiredText),
                            }),

                            assesment: yup.string().when('selfLeft', {
                                is: '1',
                                then: yup.string().required(requiredText),
                            }),

                            mood: yup.string().required(requiredText),
                        }),
                    };

                    if (isMinor) {
                        fieldsSummary.responsible = yup.object({
                            fullName: yup.string().required(requiredText),
                            birthdate: yup.string().required(requiredText).typeError(requiredText),
                            passportSerial: yup.string().required(requiredText),
                            passportNumber: yup.string().required(requiredText),
                            passportIssued: yup.string().required(requiredText),
                            passportAddress: yup.string().required(requiredText),
                            address: yup.string().required(requiredText),
                            phone: yup.string().required(requiredText),
                            email: yup.string().required(requiredText),
                            isResponsibleChecked: yup.bool().isTrue(requiredText),
                        });

                        fieldsSummary.main = yup.object({
                            fullNameGenitive: yup.string().required(requiredText),
                            passportAddress: yup.string().required(requiredText),
                            address: yup.string().required(requiredText),
                            passportSerial: yup.string().required(requiredText),
                            passportNumber: yup.string().required(requiredText),
                            passportIssued: yup.string().required(requiredText),
                            phone: yup.string().required(requiredText),
                            vk: yup.string(),
                            telegram: yup.string(),
                        });

                        fieldsSummary.criticalContacts = yup.array().of(
                            yup.object({
                                name: yup.string().required(requiredText),
                                birthdate: yup
                                    .string()
                                    .required(requiredText)
                                    .typeError(requiredText),
                                passportSerial: yup.string().required(requiredText),
                                passportNumber: yup.string().required(requiredText),
                                passportIssued: yup.string().required(requiredText),
                                passportAddress: yup.string().required(requiredText),
                                address: yup.string().required(requiredText),
                                phone: yup.string().required(requiredText),
                                email: yup.string().required(requiredText),
                            }),
                        );
                    }

                    return yup.object({
                        summary: yup.object().when('currentStep', {
                            is: 'summary',
                            then: yup.object(fieldsSummary),
                        }),

                        medical: yup.object().when('currentStep', {
                            is: 'medical',
                            then: yup.object({
                                diets: yup.string().required(requiredText),
                                proneToColdBool: yup.string().required(requiredText),
                                fractures: yup.string().required(requiredText),
                                headInjuries: yup.string().required(requiredText),
                                allergicDiseases: yup.string().required(requiredText),
                                allergyMedications: yup.string().required(requiredText),
                                chronicDiseases: yup.string().required(requiredText),
                                medications: yup.string().required(requiredText),
                                heatTransfer: yup.string().required(requiredText),
                                insectBites: yup.string().required(requiredText),
                                healthSpecials: yup.string(),
                                fatigueBool: yup.string().required(requiredText),
                                motionSicknessBool: yup.string().required(requiredText),
                                wearingGlassesBool: yup.string().required(requiredText),
                                fearDarkBool: yup.string().required(requiredText),
                                otherSpecials: yup.string(),
                            }),
                        }),

                        individual: yup.object().when('currentStep', {
                            is: 'specials',
                            then: yup.object({
                                wantToServe: yup.string().required(requiredText),
                                sports: yup.string().required(requiredText),
                                pleasureActivities: yup.string().required(requiredText),
                                specialSkills: yup.string(),
                                unPleasureActivities: yup.string().required(requiredText),
                                stressfulSituations: yup.string().required(requiredText),
                                attitudeThings: yup.string().required(requiredText),
                                selfCare: yup.string().required(requiredText),
                                preferCommunicate: yup.string().required(requiredOption),
                                communationDifficulties: yup.string().required(requiredText),
                                characterTraits: yup.string().required(requiredText),
                                badHabits: yup.string().required(requiredText),
                            }),
                        }),
                    });
                }}
                onSubmit={handleSubmit}
            >
                {formikProps => {
                    const { values } = formikProps;

                    const hasResult = values.originalStudentOnlineApplication.isFinalStatus();

                    return (
                        <>
                            <StudentOnlineApplicationHeader />

                            <Container>
                                {hasResult ? (
                                    <StudentOnlineApplicationResult />
                                ) : (
                                    renderSteps(values.currentStep)
                                )}
                            </Container>

                            {!hasResult ? (
                                <Footer>
                                    <StudentOnlineApplicationFooter />
                                </Footer>
                            ) : null}
                        </>
                    );
                }}
            </ExtendedFormik>
        </Root>
    );
};

const Root = styled.div`
    width: 100%;

    > * {
        margin-bottom: 20px;
    }

    ${breakpoints.down(800)} {
        padding: 0 8px;
    }
`;

const StyledLoader = styled(Loader)`
    padding: 24px;
`;

const Container = styled.div`
    margin-top: 20px;

    > * {
        margin-bottom: 20px;
    }
`;

const Footer = styled.div``;

export { StudentOnlineApplication };
